import * as React from 'react';
import Image from 'next/image';

import { useTranslation } from 'react-i18next';
import styles from './PinCard.module.css';
import { getImageUrl } from 'lib/util/imageUrl';
import { AnimatedButton } from 'components/Map/AnimatedButton';
import { DigitalMapPin } from 'models/digitalMap';
import { smoothMoveCamera } from 'components/Map/mapUtils';
import { useMapDisplayContext } from 'components/Map/MapDisplayContext';
import { useCurrentPosition } from 'hooks/useCurrentPosition';
import MapDisplayContextService from 'components/Map/MapDisplayContextService';

interface Props {
  pin: DigitalMapPin;
  onCloseChat: () => void;
}

export const PinCard = ({ pin, onCloseChat }: Props) => {
  const { t } = useTranslation();
  const { pushUrlParams } = useMapDisplayContext();
  const { currentPosition } = useCurrentPosition();

  const imageUrl = pin.media_items.find((item) => item.type === 'IMAGE')?.url || '';

  const handleShowOnMap = (e: React.MouseEvent) => {
    e.preventDefault();
    pushUrlParams({ pin: pin.key, pind: 'SUMMARY', nav: 'map' });
    onCloseChat();

    smoothMoveCamera(
      MapDisplayContextService.getMapDisplayContext()?.googleMap ?? null,
      MapDisplayContextService.getMapDisplayContext()?.openLayersMap ?? null,
      {
        center: {
          lat: pin?.location?.latitude ?? 0,
          lng: pin?.location?.longitude ?? 0,
        },
      }
    );
  };

  const handleGetDirections = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentPosition) {
      MapDisplayContextService.onGetDirections(currentPosition, pin);
      onCloseChat();
    }
  };

  return (
    <>
      <section className={styles['product-card']}>
        {imageUrl ? (
          <Image
            priority
            alt=""
            width={200}
            height={148}
            className={styles['product-image']}
            key={imageUrl}
            src={imageUrl}
            loader={({ width, src }) => getImageUrl(src, width * 1.5)}
          />
        ) : (
          <div className={styles['placeholder-container']}>
            <img
              src="/static/images/map/map_pin_bg.svg"
              alt=""
              className={styles['placeholder-icon']}
            />
          </div>
        )}
        <div className={styles['product-content']}>
          <h3 className={styles['product-title']}>{pin.title}</h3>
          {pin.description && <p className={styles['product-description']}>{pin.description}</p>}
          <AnimatedButton
            className={styles['product-button']}
            clickedClassName={styles['clicked']}
            onClick={handleShowOnMap}
          >
            {t('Show on Map')}
          </AnimatedButton>
          {currentPosition && (
            <AnimatedButton
              className={styles['get-directions-button']}
              clickedClassName={styles['clicked']}
              onClick={handleGetDirections}
            >
              <img
                src="/static/images/map/map_pin_bold.svg"
                alt=""
                className={styles['action-icon']}
              />
              {t('Get Directions')}
            </AnimatedButton>
          )}
        </div>
      </section>
    </>
  );
};
